import { useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import { toast } from "react-toastify";
import myInfoAtom from "../atoms/myInfoAtom";
import postData from "../custom_functions/postData";
import { url } from "../utils/config";

const roleOptions = [
    { value: "manager", label: "Manager" },
    { value: "viewer", label: "Viewer" },
];

const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
];

function AddTeamMember({ onClose, onSuccess, selectedFilter }) {
    const myInfo = useRecoilValue(myInfoAtom);
    const [isSaving, setIsSaving] = useState(false);

    const defaultStatus = useMemo(() => {
        if (selectedFilter === "active" || selectedFilter === "inactive") {
            return selectedFilter;
        }
        return "active";
    }, [selectedFilter]);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        role: "viewer",
        phone: "",
        status: defaultStatus,
    });

    function handleFormChange(event) {
        const { name, value } = event.target;
        
        // Phone number validation - limit to 15 digits max and only allow valid characters
        if (name === "phone") {
            // Only allow digits, spaces, dashes, plus, and parentheses
            const sanitized = value.replace(/[^0-9+\-\s()]/g, "");
            // Check if digits exceed 15
            const digitsOnly = sanitized.replace(/\D/g, "");
            if (digitsOnly.length > 15) {
                return; // Don't update if exceeds 15 digits
            }
            setFormData((prev) => ({ ...prev, [name]: sanitized }));
            return;
        }
        
        setFormData((prev) => ({ ...prev, [name]: value }));
    }

    function resetForm() {
        setFormData({ name: "", email: "", role: "viewer", phone: "", status: defaultStatus });
    }

    function handleClose() {
        if (isSaving) return;
        resetForm();
        onClose?.();
    }

    function formatPayload() {
        const trimmedName = formData.name.trim();
        const trimmedEmail = formData.email.trim();
        const trimmedPhone = formData.phone.trim();

        if (!trimmedName) {
            toast.error("Name is required", { autoClose: 3000 });
            return null;
        }

        // Email regex validation - requires valid format with .com, .net, .org, etc.
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!trimmedEmail || !emailRegex.test(trimmedEmail)) {
            toast.error("Enter a valid email address", { autoClose: 3000 });
            return null;
        }

        if (!trimmedPhone) {
            toast.error("Contact number is required", { autoClose: 3000 });
            return null;
        }

        // Contact number validation - only digits, spaces, dashes, plus, and parentheses allowed
        const phoneRegex = /^[0-9+\-\s()]+$/;
        if (!phoneRegex.test(trimmedPhone)) {
            toast.error("Contact number should only contain numbers", { autoClose: 3000 });
            return null;
        }

        // Remove non-digit characters for length check
        const digitsOnly = trimmedPhone.replace(/\D/g, "");
        if (digitsOnly.length < 7) {
            toast.error("Enter a valid contact number", { autoClose: 3000 });
            return null;
        }

        const fleetCustomerId = myInfo?.id || myInfo?.fleet_customer_id;
        if (!fleetCustomerId) {
            toast.error("Missing fleet customer information", { autoClose: 3000 });
            return null;
        }

        return {
            fleet_customer_id: fleetCustomerId,
            name: trimmedName,
            email: trimmedEmail,
            role: formData.role,
            phone: trimmedPhone || null,
            status: formData.status,
        };
    }

    function handleSubmit(event) {
        event.preventDefault();
        const payload = formatPayload();
        if (!payload) {
            return;
        }

        setIsSaving(true);
        postData(
            `${url}/team_members`,
            payload,
            (error) => {
                setIsSaving(false);
                toast.error(error?.response?.data?.message || "Unable to add team member", { autoClose: 3000 });
            },
            () => {
                setIsSaving(false);
                toast.success("Team member added", { autoClose: 3000 });
                resetForm();
                onSuccess?.();
            }
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-emerald-900/40 backdrop-blur-sm px-4">
            <div className="w-full max-w-md rounded-3xl border border-emerald-100 bg-white p-6 shadow-[0_18px_48px_rgba(22,101,52,0.18)]">
                <div className="flex items-center justify-between mb-4">
                    <div className="text-lg font-semibold text-emerald-900">Add Team Member</div>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="h-8 w-8 flex items-center justify-center rounded-full text-emerald-700 hover:bg-emerald-50"
                        aria-label="Close add team member modal"
                    >
                        ×
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-semibold text-emerald-800 mb-2">Full Name</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleFormChange}
                            className="w-full rounded-xl border border-emerald-200 bg-white px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                            placeholder="Enter team member name"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-emerald-800 mb-2">Contact Number</label>
                        <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleFormChange}
                            maxLength={14}
                            className="w-full rounded-xl border border-emerald-200 bg-white px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                            placeholder="Enter phone number"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-emerald-800 mb-2">Email</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleFormChange}
                            className="w-full rounded-xl border border-emerald-200 bg-white px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                            placeholder="name@example.com"
                        />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <label className="block text-sm font-semibold text-emerald-800 mb-2">Role</label>
                            <select
                                name="role"
                                value={formData.role}
                                onChange={handleFormChange}
                                className="w-full rounded-xl border border-emerald-200 bg-white px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                            >
                                {roleOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-emerald-800 mb-2">Status</label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleFormChange}
                                className="w-full rounded-xl border border-emerald-200 bg-white px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                            >
                                {statusOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            type="button"
                            className="px-5 py-2 rounded-full border border-emerald-200 text-emerald-700 text-sm font-semibold hover:bg-emerald-50"
                            onClick={handleClose}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={`px-5 py-2 rounded-full text-white text-sm font-semibold transition-colors ${isSaving ? "bg-emerald-300" : "bg-emerald-600 hover:bg-emerald-700"}`}
                            disabled={isSaving}
                        >
                            {isSaving ? "Saving..." : "Save Member"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default AddTeamMember;
